import { useState, useEffect, useCallback } from "react";
import {
  Checkbox,
  ComposedModal,
  IconButton,
  ModalBody,
  ModalHeader,
  Tag,
} from "@carbon/react";
import { CheckmarkFilled, Email } from "@carbon/icons-react";
import i18next from "../../../i18n";
import InviteSupplier from "../../../components/Material/InviteSupplier/InviteSupplier";
import { BuyerSellerResponse, OrderToSubmitRequest } from "../../../types";
import { OrderInvitationContextType } from "../../../context/types";
import PotentialSuppliersSection from "../PotentialSuppliersSection/PotentialSuppliersSection";
import { usePotentialSuppliers } from "../../../hooks/useSuppliers";
import { useUser } from "../../../context";
import { getCompanyLegalAddressLabel } from "../../../utils/address";
import { deriveMaterialSelectedSuppliers } from "../supplierRows";

const SUPPLIERS_SORT = { true: 1, false: 2 };

type MaterialSuppliersSectionProps = {
  material: {
    id: string;
    ean: string;
    globalMaterialName: string;
    deliveryPlace?: { name: string };
  };
  suppliers: BuyerSellerResponse[];
  orderInvitation: OrderInvitationContextType[];
  ordersToSubmit: OrderToSubmitRequest[];
  selectedComplexType?: any;
  selectAllTrigger?: boolean;
  handleSupplierSelection: (
    supplier: BuyerSellerResponse,
    materialId: string
  ) => void;
  handleOrdersToSubmit: (
    invite:
      | { emailsList: string; materialId: string }
      | {
          materialId: string;
          supplierId: string;
          supplierInfo: {
            id: string;
            email: string;
            name: string;
            companyName: string;
            validated: boolean;
          };
        },
    type?: any
  ) => void;
};

const MaterialSuppliersSection = ({
  material,
  suppliers,
  orderInvitation,
  ordersToSubmit,
  selectedComplexType,
  selectAllTrigger,
  handleSupplierSelection,
  handleOrdersToSubmit,
}: MaterialSuppliersSectionProps) => {
  const [open, setOpen] = useState(false);
  const [potentialSuppliersPage, setPotentialSuppliersPage] = useState(0);
  const { user } = useUser();

  const {
    mutate: getPotentialSuppliers,
    data: potentialSuppliers,
    isPending: isLoadingPotentialSuppliers,
  } = usePotentialSuppliers();

  useEffect(() => {
    if (material.id && user?.company?.country?.id) {
      const countryId = user.company.country.id;
      getPotentialSuppliers({
        countryId,
        page: potentialSuppliersPage,
        materialIds: [material.id],
      });
    }
  }, [
    material.id,
    potentialSuppliersPage,
    user?.company?.country?.id,
    getPotentialSuppliers,
  ]);

  const handleSelectAllForMaterial = useCallback(() => {
    const currentOrder = ordersToSubmit.find(
      (order: any) => order.id === material.id
    );

    const hasAnySelected =
      currentOrder?.suppliers && currentOrder.suppliers.length > 0;

    if (hasAnySelected) {
      const buildClearData = {
        materialId: material.id,
        supplierId: "clear-all",
        supplierInfo: {
          id: "clear-all",
          email: "",
          name: "",
          companyName: "",
          validated: false,
        },
      };
      handleOrdersToSubmit(buildClearData, "clearAllForMaterial");
    } else {
      suppliers.forEach((supplier: any) => {
        const alreadySelected = currentOrder?.suppliers?.find(
          (sup: any) => sup?.id === supplier?.seller?.id
        );

        if (!alreadySelected) {
          handleSupplierSelection(supplier, material.id);
        }
      });

      if (potentialSuppliers?.content) {
        potentialSuppliers.content.forEach((supplier: any) => {
          const alreadySelected = currentOrder?.suppliers?.find(
            (sup: any) => sup?.id === supplier?.user?.id
          );

          if (!alreadySelected) {
            const supplierData = {
              materialId: material.id,
              supplierId: supplier.user.id,
              supplierInfo: {
                id: supplier.user.id,
                email: supplier.user.email,
                name: `${supplier.user.firstName || ""} ${supplier.user.lastName || ""}`.trim(),
                companyName: supplier.user.company?.name || "",
                validated: supplier.validated,
              },
            };
            handleOrdersToSubmit(supplierData, "existingSupplier");
          }
        });
      }
    }
  }, [
    ordersToSubmit,
    material.id,
    suppliers,
    potentialSuppliers?.content,
    handleOrdersToSubmit,
    handleSupplierSelection,
  ]);

  useEffect(() => {
    if (selectAllTrigger !== undefined) {
      handleSelectAllForMaterial();
    }
  }, [selectAllTrigger, handleSelectAllForMaterial]);

  const handleClick = () => {
    setOpen(true);
  };

  const onCloseInvitation = () => setOpen(false);

  const currentOrder = ordersToSubmit.find(
    (order: any) => order.id === material.id
  );

  const materialOrderInvitations = orderInvitation.filter(
    (item: any) => item.materialId === material.id
  );

  const hasSuppliers = suppliers && suppliers.length > 0;
  const hasNoSuppliersData =
    !hasSuppliers && materialOrderInvitations.length === 0;

  const selectedSuppliers = deriveMaterialSelectedSuppliers(
    currentOrder,
    suppliers
  );

  const handleRemoveSelectedSupplier = (supplier: any) => {
    if (supplier.type === "existing") {
      if (supplier.supplierData?.seller) {
        handleSupplierSelection(supplier.supplierData, material.id);
      } else {
        handleOrdersToSubmit(
          {
            materialId: material.id,
            supplierId: supplier.id,
            supplierInfo: {
              id: supplier.id,
              email: supplier.email,
              name: supplier.name,
              companyName: supplier.name,
              validated: supplier.validated,
            },
          },
          "removeExistingSupplier"
        );
      }
    } else if (supplier.type === "email") {
      const parentEmailsList =
        supplier.supplierData?.emailsList ?? supplier.email;
      const allEmails = String(parentEmailsList)
        .split(":")
        .map((e: string) => e.trim())
        .filter(Boolean);

      if (allEmails.length <= 1) {
        handleOrdersToSubmit({
          materialId: material.id,
          emailsList: parentEmailsList,
        });
      } else {
        handleOrdersToSubmit({
          materialId: material.id,
          emailsList: parentEmailsList,
        });
        allEmails
          .filter((email: string) => email !== supplier.email)
          .forEach((email: string) => {
            handleOrdersToSubmit({
              materialId: material.id,
              emailsList: email,
            });
          });
      }
    }
  };

  const selectedSupplierIds = selectedSuppliers
    .filter((s) => s.type === "existing")
    .map((s) => s.id);

  const availableSuppliers = suppliers.filter(
    (supplier: any) => !selectedSupplierIds.includes(supplier.seller.id)
  );

  return (
    <div className="order-supplier-section">
      <div className="order-supplier-section__head">
        <span className="order-supplier-section__title">
          {material.ean}
          {" - "}
          <span className="order-supplier-section__name">
            {material.globalMaterialName}
          </span>
          {" - "}
          {material.deliveryPlace
            ? `(${material.deliveryPlace.name})`
            : `(${i18next.t("messages.toAgree")})`}
        </span>
        <IconButton
          kind="ghost"
          size="sm"
          label={i18next.t("invite.invite")}
          align="left"
          onClick={handleClick}
        >
          <Email />
        </IconButton>
      </div>

      {selectedSuppliers.length > 0 && (
        <div className="order-supplier-group">
          {selectedSuppliers.map((supplier) => {
            const address = getCompanyLegalAddressLabel(
              supplier.supplierData?.seller?.company
            );
            return (
              <Checkbox
                key={`selected-${supplier.id}`}
                id={`selected-${material.id}-${supplier.id}`}
                checked={true}
                onChange={() => handleRemoveSelectedSupplier(supplier)}
                labelText={
                  <span className="order-supplier-card__name">
                    {supplier.name}
                    {supplier.validated && (
                      <CheckmarkFilled className="order-supplier-card__badge" />
                    )}
                    {supplier.type === "email" && (
                      <Tag type="warm-gray" size="sm">
                        {i18next.t("offer.invited")}
                      </Tag>
                    )}
                    {address && (
                      <span className="order-supplier-card__addr">
                        {address}
                      </span>
                    )}
                  </span>
                }
              />
            );
          })}
        </div>
      )}

      {hasSuppliers && (
        <div className="order-supplier-group">
          {availableSuppliers
            .sort(
              (a: { validated: boolean }, b: { validated: boolean }) =>
                SUPPLIERS_SORT[`${a.validated}`] -
                SUPPLIERS_SORT[`${b.validated}`]
            )
            .map((supplier: any) => {
              const isChecked = currentOrder?.suppliers?.find(
                (sup: any) => sup?.id === supplier?.seller?.id
              );
              const address = getCompanyLegalAddressLabel(
                supplier.seller.company
              );
              return (
                <Checkbox
                  key={supplier.id}
                  id={`available-${material.id}-${supplier.id}`}
                  checked={!!isChecked}
                  onChange={() =>
                    handleSupplierSelection(supplier, material.id)
                  }
                  labelText={
                    <span className="order-supplier-card__name">
                      {supplier.seller.company?.name ||
                        `${supplier.seller.firstName || ""} ${supplier.seller.lastName || ""}`.trim()}
                      {supplier.validated && (
                        <CheckmarkFilled className="order-supplier-card__badge" />
                      )}
                      {address && (
                        <span className="order-supplier-card__addr">
                          {address}
                        </span>
                      )}
                    </span>
                  }
                />
              );
            })}
        </div>
      )}

      {hasNoSuppliersData &&
        selectedSuppliers.length === 0 &&
        (!potentialSuppliers?.content ||
          potentialSuppliers.content.length === 0) && (
          <p className="order-supplier-section__title">
            {i18next.t("invite.without")}
          </p>
        )}

      <PotentialSuppliersSection
        potentialSuppliers={potentialSuppliers}
        ordersToSubmit={ordersToSubmit}
        selectedComplexType={selectedComplexType}
        materialId={material.id}
        isLoading={isLoadingPotentialSuppliers}
        handleOrdersToSubmit={handleOrdersToSubmit}
        potentialSuppliersPage={potentialSuppliersPage}
        setPotentialSuppliersPage={setPotentialSuppliersPage}
      />

      <ComposedModal open={open} onClose={() => setOpen(false)} size="sm">
        <ModalHeader title={i18next.t("orderByRequest.inviteSupplier")} />
        <ModalBody hasForm>
          <InviteSupplier
            isOrder
            materialId={material.id}
            materialName={material.globalMaterialName}
            onCloseInvitation={onCloseInvitation}
            orderInvitation={orderInvitation}
            handleOrdersToSubmit={handleOrdersToSubmit}
          />
        </ModalBody>
      </ComposedModal>
    </div>
  );
};

export default MaterialSuppliersSection;
